import {
    LocalizedMetadata,
    AppStoreType,
    ReleaseStepType,
    MetadataTranslationLocale,
    GeneratedReleaseNotesStepContext,
    GeneratedReleaseNotesMarkdownContext,
    MetadataAvailableTokens,
    MetadataTranslationStatus,
} from '@/types';
import { createWhatsNewFormItem } from './MetadataDefaultFields';
import { ExtendedForm, ExtendedFormObject } from '@/utils/ExtendedForm';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Collapse from 'antd/lib/collapse';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import Icon, {
    CaretRightOutlined,
    InfoCircleOutlined,
    SaveOutlined,
    TranslationOutlined,
    ThunderboltFilled,
    DownOutlined,
} from '@ant-design/icons';
import { runwayColors, antGrey, antGold, hexStringToRGBAString } from '@/components/styles/globalStyles';
import { EmptyStateWrapper } from '@/components/releaseEmptyState/EmptyStateWrapper';
import { FeatureSection, Nullable } from '@/typescriptHelpers';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { getIsAppleAppStore, getIsGooglePlayStore } from '@/helpers';
import * as React from 'react';
import { CharCounter } from './metadata/CharCounter';
import { GenerateReleaseNotesDropdown } from './sharedComponents/GenerateReleaseNotesDropdown';
import TextArea from 'antd/lib/input/TextArea';
import Popover from 'antd/lib/popover';
import { PatternTokenHelperTextComponent } from './appSettings/PatternTokenHelperTextComponent';
import Divider from 'antd/lib/divider';
import Dropdown from 'antd/lib/dropdown';
import {
    displayConsoleNameForAppStoreType,
    displayMetadataFieldTranslationsStatus,
    getColorForMetadataFieldTranslationsStatus,
    getIconForMetadataFieldTranslationsStatus,
    getMetadataFieldTranslationsStatusTooltipMessage,
} from '@/helpers/displayHelpers';

export const MetadataFieldLocalizationStatusComponent = ({
    status,
    appStoreName,
    translationsIntegrationProviderName,
    appStoreVersionExists,
}: {
    status: MetadataTranslationStatus;
    appStoreName: string;
    translationsIntegrationProviderName: string;
    appStoreVersionExists: boolean;
}) => {
    if (!translationsIntegrationProviderName) {
        return null;
    }

    return (
        <div>
            <TranslationOutlined style={{ fontSize: 16, color: antGrey.grey7, marginRight: 8 }} />
            <Tooltip
                title={
                    <div>
                        <Text strong style={{ color: runwayColors.white }}>
                            {displayMetadataFieldTranslationsStatus(status)}
                        </Text>
                        <Text style={{ color: runwayColors.white }}>
                            {' '}
                            -{' '}
                            {getMetadataFieldTranslationsStatusTooltipMessage(
                                status,
                                appStoreName,
                                translationsIntegrationProviderName,
                                appStoreVersionExists,
                            )}
                        </Text>
                    </div>
                }
            >
                <Icon
                    component={getIconForMetadataFieldTranslationsStatus(status)}
                    style={{
                        fontSize: 16,
                        color: getColorForMetadataFieldTranslationsStatus(status),
                        marginRight: 8,
                    }}
                />
            </Tooltip>
        </div>
    );
};

export interface MetadataFormValues extends LocalizedMetadata {}

enum CollapseKeys {
    ReleaseNotes = 'releaseNotes',
    AppName = 'appName',
    Subtitle = 'subtitle',
    VideoUrl = 'videoUrl',
    KeywordsOrShortDescription = 'keywordsOrShortDescription',
    PromoText = 'promoText',
    Description = 'description',
}

interface TranslationsWarningBannerProps {
    isSourceLocale: boolean;
    isAutomationEnabled: boolean;
    integrationName: string;
}

const TranslationsWarningBanner: React.FC<TranslationsWarningBannerProps> = ({
    isSourceLocale,
    isAutomationEnabled,
    integrationName,
}) => {
    const renderTranslationsWarningBanner = (
        style: React.CSSProperties,
        icon: React.ReactNode,
        text: React.ReactNode,
    ) => {
        const bannerStyle: React.CSSProperties = {
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            borderRadius: '5px',
            alignItems: 'flex-start',
            padding: 8,
            marginTop: 25,
            marginLeft: 15,
            marginRight: 135,
        };

        return (
            <div style={{ ...bannerStyle, ...style }}>
                {icon}
                {text}
            </div>
        );
    };

    if (!isSourceLocale) {
        return renderTranslationsWarningBanner(
            {
                backgroundColor: antGold.gold1,
                border: `1px solid ${antGold.gold3}`,
            },
            <TranslationOutlined
                style={{ paddingTop: 4, marginLeft: 7, marginRight: 15, color: runwayColors.statusYellow }}
            />,
            <Text style={{ fontWeight: 400, color: antGrey.grey8 }}>
                This is not your source language. Saved changes made here could be overwritten by future exported
                translations from {integrationName}.
            </Text>,
        );
    } else {
        if (isAutomationEnabled) {
            return renderTranslationsWarningBanner(
                {
                    backgroundColor: hexStringToRGBAString(runwayColors.primary, 0.06),
                    border: `1px solid ${hexStringToRGBAString(runwayColors.primary, 0.31)}`,
                },
                <ThunderboltFilled
                    style={{ paddingTop: 5, marginLeft: 7, marginRight: 15, color: runwayColors.primary }}
                />,
                <Text style={{ fontWeight: 400, color: antGrey.grey8 }}>
                    This is your source language. Saved changes made here will be uploaded to {integrationName} to be
                    translated to your other languages.
                </Text>,
            );
        } else {
            return renderTranslationsWarningBanner(
                {
                    backgroundColor: hexStringToRGBAString(runwayColors.primary, 0.06),
                    border: `1px solid ${hexStringToRGBAString(runwayColors.primary, 0.31)}`,
                },
                <TranslationOutlined
                    style={{ paddingTop: 5, marginLeft: 7, marginRight: 15, color: runwayColors.primary }}
                />,
                <Text style={{ fontWeight: 400, color: antGrey.grey8 }}>
                    This is your source language. After saving your changes, use the &apos;Sync&apos; action above to
                    upload changes to {integrationName} for translation to your other languages.
                </Text>,
            );
        }
    }
};

interface StepContentMetadataFormProps {
    appId: string;
    releaseId: string;
    orgId: string;
    appStoreType: AppStoreType;
    releaseStepType: ReleaseStepType;

    initialValues: MetadataFormValues;
    extendedFormObject: ExtendedFormObject<MetadataFormValues>;

    displayLocale: string;
    locale: string;

    isLoading: boolean;
    isDirty: boolean;
    isInvalid: boolean;
    defaultReleaseNotesByLocale: Record<string, string>;
    isLoadingGetPreviousReleaseNotes: boolean;

    shouldDisableMetadataEditing: Nullable<boolean>;
    ctaButtonDisplayLocale: string;

    onClickShowFeaturesList: () => void;
    onClickSetDefaultReleaseNotesForLocale: (id?: string) => void;
    onClickSetBetaTesterNotesForLocale: (id?: string) => void;
    onClickSetPreviousReleaseNotesForLocale: (id?: string) => void;
    onClickSaveChangesForLocalization: (id?: string) => void;
    onApplyFieldToAllLocales: (id: string, displayLocale: string, field: keyof MetadataFormValues) => void;

    // Translation integration
    translationStatus: Nullable<MetadataTranslationLocale>;
    isSourceLocale: boolean;
    isTranslationsAutomationEnabled: boolean;
    translationsIntegrationName: string;
    shouldShowTranslationsWarningBanner: boolean;
    appStoreVersionExists: boolean;
}

interface CreateCollapsiblePanelProps {
    id: string;
    displayLocale: string;
    key: string;
    title: string;
    featureSection: FeatureSection;
    children: React.ReactNode;
    appId: string;
    releaseId: string;
    releaseStepType: ReleaseStepType;
    isDisabled: boolean;
    fieldName: keyof MetadataFormValues;
    maxLength: number;
    appStoreType: AppStoreType;
    integrationName: string;
    appStoreVersionExists: boolean;
    onApplyChangesToAllLocales: (id: string, displayLocale: string, field: keyof MetadataFormValues) => void;
    translationStatus: Nullable<MetadataTranslationLocale>;
    tooltip?: string;
    minLength?: number;
    titleExtra?: React.ReactNode;
    bottomExtra?: React.ReactNode;
    topButtonsExtra?: React.ReactNode;
    bottomButtonsExtra?: React.ReactNode;
}

const createCollapsiblePanel = ({
    id,
    key,
    title,
    tooltip,
    featureSection,
    children,
    releaseStepType,
    appId,
    releaseId,
    translationStatus,
    appStoreType,
    integrationName,
    appStoreVersionExists,
    onApplyChangesToAllLocales,
    isDisabled,
    fieldName,
    maxLength,
    titleExtra,
    bottomExtra,
    topButtonsExtra,
    bottomButtonsExtra,
    minLength,
    displayLocale,
}: CreateCollapsiblePanelProps) => {
    return (
        <Collapse.Panel
            key={key}
            header={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <Text
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: antGrey.grey8,
                            }}
                        >
                            {title}
                        </Text>
                        {tooltip && (
                            <Tooltip title={tooltip}>
                                <InfoCircleOutlined style={{ color: runwayColors.info, marginLeft: 6 }} />
                            </Tooltip>
                        )}
                        {titleExtra}
                        <EmptyStateWrapper
                            releaseStepType={releaseStepType}
                            customWrapperStyle={{
                                width: 44,
                                height: 22,
                            }}
                            placement="right"
                            appId={appId}
                            shouldHideDotOnHover={false}
                            featureSections={[featureSection]}
                            releaseId={releaseId}
                            shouldShowAsTooltip
                        />
                    </Space>
                </div>
            }
        >
            <div style={{ display: 'flex', gap: 24 }}>
                <div style={{ flexGrow: 1 }}>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <div
                                style={{
                                    flexGrow: 1,
                                    position: 'relative',
                                }}
                            >
                                <Space
                                    style={{
                                        position: 'absolute',
                                        right: 0,
                                        bottom: 'calc(100% + 12px)',
                                    }}
                                    size={12}
                                >
                                    {translationStatus && (
                                        <MetadataFieldLocalizationStatusComponent
                                            status={translationStatus[fieldName]}
                                            appStoreName={displayConsoleNameForAppStoreType(appStoreType)}
                                            translationsIntegrationProviderName={integrationName}
                                            appStoreVersionExists={appStoreVersionExists}
                                        />
                                    )}
                                    {topButtonsExtra}
                                </Space>
                                {children}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'baseline',
                                    justifyContent: 'space-between',
                                    marginTop: -8,
                                }}
                            >
                                <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start' }}>
                                    {bottomExtra}
                                </div>
                                <Space
                                    style={{ flexShrink: 0 }}
                                    size={2}
                                    split={
                                        <Divider
                                            style={{ backgroundColor: antGrey.grey5, margin: 0 }}
                                            type="vertical"
                                        />
                                    }
                                >
                                    {bottomButtonsExtra}
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => onApplyChangesToAllLocales?.(id, displayLocale, fieldName)}
                                        disabled={isDisabled}
                                    >
                                        Copy this text to all locales
                                    </Button>
                                </Space>
                            </div>
                        </div>
                        <div style={{ minWidth: 80 }}>
                            <Form.Item shouldUpdate noStyle>
                                {(form) => {
                                    const stringValue = form.getFieldValue(fieldName);
                                    return (
                                        <CharCounter
                                            stringValue={stringValue}
                                            maxLength={maxLength}
                                            minLength={minLength}
                                        />
                                    );
                                }}
                            </Form.Item>
                        </div>
                    </div>
                </div>
            </div>
        </Collapse.Panel>
    );
};

export const StepContentMetadataForm: React.FC<StepContentMetadataFormProps> = React.memo((props) => {
    const isAppleStore = getIsAppleAppStore(props.appStoreType);
    const isGooglePlayStore = getIsGooglePlayStore(props.appStoreType);
    const releaseNotesTitle = isAppleStore ? `What's New in this Version` : `Release Notes`;

    const releaseNotesMaxLength = getIsAppleAppStore(props.appStoreType) ? 4000 : 500;
    const keywordsMaxLength = isAppleStore ? 100 : 80;
    const promoTextMaxLength = 170;
    const appNameMaxLength = 30;
    const subtitleMaxLength = 30;
    const videoUrlMaxLength = 1000;
    const descriptionMaxLength = 4000;

    return (
        <ExtendedForm initialValues={props.initialValues} extendedFormObject={props.extendedFormObject}>
            <div style={{ paddingLeft: 24, paddingRight: 24 }}>
                {props.shouldShowTranslationsWarningBanner && (
                    <TranslationsWarningBanner
                        isSourceLocale={props.isSourceLocale ?? false}
                        isAutomationEnabled={props.isTranslationsAutomationEnabled ?? false}
                        integrationName={props.translationsIntegrationName ?? ''}
                    />
                )}

                <Collapse
                    defaultActiveKey={Object.values(CollapseKeys)}
                    bordered={false}
                    expandIcon={({ isActive }) => (
                        <CaretRightOutlined
                            style={{
                                color: antGrey.grey8,
                                fontSize: 16,
                            }}
                            rotate={isActive ? 90 : 0}
                        />
                    )}
                    destroyInactivePanel={false}
                    className="metadata-collapse"
                    style={{
                        backgroundColor: 'transparent',
                    }}
                >
                    <div
                        style={{
                            marginTop: 24,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            width: '100%',
                            marginBottom: 24,
                        }}
                    >
                        <Space align="center" style={{ padding: 15 }} size={16}>
                            <PermissionedActionButton
                                releaseId={props.releaseId}
                                actionType={RunwayUserAction.UAUpdateMetadata}
                                onClick={() => props.onClickSaveChangesForLocalization(props.initialValues.id)}
                                disabled={
                                    props.shouldDisableMetadataEditing ||
                                    props.isInvalid ||
                                    !props.isDirty ||
                                    props.isLoading
                                }
                                loading={props.isLoading}
                                icon={<SaveOutlined style={{ color: runwayColors.primary }} />}
                            >
                                {props.appStoreVersionExists ?
                                    `Save ${props.ctaButtonDisplayLocale} changes`
                                :   `Save ${props.ctaButtonDisplayLocale} changes in Runway`}
                            </PermissionedActionButton>
                        </Space>
                    </div>

                    {/* Release Notes */}
                    {createCollapsiblePanel({
                        id: props.initialValues.id,
                        key: CollapseKeys.ReleaseNotes,
                        displayLocale: props.displayLocale,
                        title: releaseNotesTitle,
                        tooltip: undefined,
                        featureSection: FeatureSection.MetadataReleaseNotesContent,
                        titleExtra: (
                            <Form.Item shouldUpdate noStyle>
                                {(form) => {
                                    const fieldValue = form.getFieldValue('whatsNew');

                                    const defaultReleaseNotes =
                                        props.defaultReleaseNotesByLocale[props.initialValues.id];
                                    const isDefaultReleaseNotes =
                                        defaultReleaseNotes && fieldValue === defaultReleaseNotes;
                                    return (
                                        isDefaultReleaseNotes && (
                                            <Tooltip
                                                title={
                                                    <Text
                                                        style={{
                                                            color: runwayColors.white,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        These are your default release notes
                                                    </Text>
                                                }
                                                overlayInnerStyle={{
                                                    width: 260,
                                                }}
                                                placement="right"
                                            >
                                                <ThunderboltFilled style={{ color: runwayColors.primary }} />
                                            </Tooltip>
                                        )
                                    );
                                }}
                            </Form.Item>
                        ),
                        topButtonsExtra: (
                            <GenerateReleaseNotesDropdown
                                orgId={props.orgId}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                stepContext={GeneratedReleaseNotesStepContext.Metadata}
                                markdownContext={GeneratedReleaseNotesMarkdownContext.MarkdownUnsupported}
                                locale={props.locale}
                            />
                        ),
                        bottomButtonsExtra: (
                            <Dropdown
                                disabled={props.shouldDisableMetadataEditing}
                                menu={{
                                    items: [
                                        {
                                            label: 'From default release notes',
                                            key: '1',
                                            onClick: () =>
                                                props.onClickSetDefaultReleaseNotesForLocale(props.initialValues.id),
                                            disabled: !props.onClickSetDefaultReleaseNotesForLocale,
                                        },
                                        {
                                            label: 'From previous release',
                                            key: '2',
                                            onClick: () =>
                                                props.onClickSetPreviousReleaseNotesForLocale(props.initialValues.id),
                                            disabled:
                                                !props.onClickSetPreviousReleaseNotesForLocale ||
                                                props.isLoadingGetPreviousReleaseNotes,
                                        },
                                        {
                                            label: 'From latest beta tester notes',
                                            key: '3',
                                            onClick: () =>
                                                props.onClickSetBetaTesterNotesForLocale(props.initialValues.id),
                                            disabled: !props.onClickSetBetaTesterNotesForLocale,
                                        },
                                    ],
                                }}
                            >
                                <Button
                                    loading={props.isLoadingGetPreviousReleaseNotes}
                                    type="link"
                                    disabled={props.shouldDisableMetadataEditing}
                                >
                                    <Space>
                                        <Text
                                            style={{
                                                color: runwayColors.primary,
                                            }}
                                        >
                                            Populate release notes for {props.displayLocale}
                                        </Text>
                                        <DownOutlined />
                                    </Space>
                                </Button>
                            </Dropdown>
                        ),
                        children: createWhatsNewFormItem({
                            appStoreType: props.appStoreType,
                            shouldDisableMetadataEditing: props.shouldDisableMetadataEditing,
                            placeholder: "Add information about what's new in this release.",
                        }),
                        bottomExtra: (
                            <Text type="secondary">
                                Pattern token strings are supported. Token strings will be rendered immediately on save.{' '}
                                <Popover
                                    content={
                                        <div style={{ overflow: 'scroll', maxWidth: '40vw', maxHeight: '40vh' }}>
                                            <PatternTokenHelperTextComponent
                                                availableTokens={MetadataAvailableTokens}
                                            />
                                        </div>
                                    }
                                    placement="top"
                                >
                                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                </Popover>
                            </Text>
                        ),
                        releaseStepType: props.releaseStepType,
                        appId: props.appId,
                        releaseId: props.releaseId,
                        onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                        isDisabled: props.shouldDisableMetadataEditing,
                        fieldName: 'whatsNew',
                        maxLength: releaseNotesMaxLength,
                        translationStatus: props.translationStatus,
                        appStoreType: props.appStoreType,
                        integrationName: props.translationsIntegrationName,
                        appStoreVersionExists: props.appStoreVersionExists,
                    })}

                    {/* Keywords (Apple) / Short Description (Google Play) */}
                    {createCollapsiblePanel({
                        id: props.initialValues.id,
                        key: CollapseKeys.KeywordsOrShortDescription,
                        displayLocale: props.displayLocale,
                        title: isAppleStore ? 'Keywords' : 'Short description',
                        tooltip:
                            isAppleStore ? undefined : (
                                'A short description for your app. Users can expand to view your full description.'
                            ),
                        featureSection: FeatureSection.MetadataKeywordsOrShortDescription,
                        children:
                            isAppleStore ?
                                <Form.Item
                                    name="keywords"
                                    rules={[
                                        {
                                            max: keywordsMaxLength,
                                            message: `Keywords must be ${keywordsMaxLength} characters or less.`,
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={'Add keywords'}
                                        disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                    />
                                </Form.Item>
                            :   <Form.Item
                                    name="shortDescription"
                                    rules={[
                                        {
                                            max: keywordsMaxLength,
                                            message: `Short description must be ${keywordsMaxLength} characters or less.`,
                                        },
                                    ]}
                                >
                                    <TextArea
                                        placeholder={'Add short description'}
                                        disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                    />
                                </Form.Item>,
                        releaseStepType: props.releaseStepType,
                        appId: props.appId,
                        releaseId: props.releaseId,
                        onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                        isDisabled: props.shouldDisableMetadataEditing || props.isLoading,
                        fieldName: isAppleStore ? 'keywords' : 'shortDescription',
                        maxLength: keywordsMaxLength,
                        translationStatus: props.translationStatus,
                        appStoreType: props.appStoreType,
                        integrationName: props.translationsIntegrationName,
                        appStoreVersionExists: props.appStoreVersionExists,
                    })}

                    {/* Promotional Text (Apple only) */}
                    {isAppleStore &&
                        createCollapsiblePanel({
                            id: props.initialValues.id,
                            key: CollapseKeys.PromoText,
                            displayLocale: props.displayLocale,
                            title: 'Promotional text',
                            tooltip:
                                'Promotional text lets you inform your App Store visitors of any current app features without requiring an updated submission. This text will appear above your description on the App Store.',
                            featureSection: FeatureSection.MetadataPromotionalText,
                            children: (
                                <Form.Item
                                    name="promotionalText"
                                    rules={[
                                        {
                                            max: promoTextMaxLength,
                                            message: `Promotional text must be ${promoTextMaxLength} characters or less.`,
                                        },
                                    ]}
                                >
                                    <TextArea
                                        placeholder={'Add promotional text'}
                                        disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                        style={{ minWidth: 500, height: 150, position: 'relative' }}
                                    />
                                </Form.Item>
                            ),
                            bottomExtra: (
                                <Text type="secondary">
                                    Promotional text does not require a submission and will be updated immediately on
                                    your App Store listing.
                                </Text>
                            ),
                            releaseStepType: props.releaseStepType,
                            appId: props.appId,
                            releaseId: props.releaseId,
                            onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                            isDisabled: props.shouldDisableMetadataEditing || props.isLoading,
                            fieldName: 'promotionalText',
                            maxLength: promoTextMaxLength,
                            minLength: 0,
                            translationStatus: props.translationStatus,
                            appStoreType: props.appStoreType,
                            integrationName: props.translationsIntegrationName,
                            appStoreVersionExists: props.appStoreVersionExists,
                        })}

                    {/* App Name */}
                    {createCollapsiblePanel({
                        id: props.initialValues.id,
                        key: CollapseKeys.AppName,
                        displayLocale: props.displayLocale,
                        title: 'App name',
                        tooltip:
                            isAppleStore ?
                                'The localized name of your app as it appears on the App Store. The name must be at least two characters and no more than 30 characters. It can be edited until you submit the app to App Review. Later, you can change the name when you create a new version or the status of the app version permits editing this property.'
                            :   'The localized name of your app as it appears on the Play Store. The name must be at least two characters and no more than 30 characters.',
                        featureSection: FeatureSection.MetadataAppName,
                        children: (
                            <Form.Item
                                name="appName"
                                rules={[
                                    {
                                        min: 2,
                                        max: appNameMaxLength,
                                        message: `App name must be between 2 and ${appNameMaxLength} characters`,
                                    },
                                ]}
                            >
                                <Input
                                    placeholder={'App name'}
                                    disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                />
                            </Form.Item>
                        ),
                        releaseStepType: props.releaseStepType,
                        appId: props.appId,
                        releaseId: props.releaseId,
                        onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                        isDisabled: props.shouldDisableMetadataEditing || props.isLoading,
                        fieldName: 'appName',
                        maxLength: appNameMaxLength,
                        minLength: 2,
                        translationStatus: props.translationStatus,
                        appStoreType: props.appStoreType,
                        integrationName: props.translationsIntegrationName,
                        appStoreVersionExists: props.appStoreVersionExists,
                    })}

                    {/* Subtitle (Apple only) */}
                    {isAppleStore &&
                        createCollapsiblePanel({
                            id: props.initialValues.id,
                            key: CollapseKeys.Subtitle,
                            displayLocale: props.displayLocale,
                            title: 'Subtitle',
                            tooltip:
                                "A summary of your app that will appear under your app's name on the App Store. This can't be longer than 30 characters.",
                            featureSection: FeatureSection.MetadataAppName,
                            children: (
                                <Form.Item
                                    name="subtitle"
                                    rules={[
                                        {
                                            max: subtitleMaxLength,
                                            message: `Subtitle must be ${subtitleMaxLength} characters or less.`,
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={'Subtitle'}
                                        disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                    />
                                </Form.Item>
                            ),
                            releaseStepType: props.releaseStepType,
                            appId: props.appId,
                            releaseId: props.releaseId,
                            onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                            isDisabled: props.shouldDisableMetadataEditing || props.isLoading,
                            fieldName: 'subtitle',
                            maxLength: subtitleMaxLength,
                            minLength: 0,
                            translationStatus: props.translationStatus,
                            appStoreType: props.appStoreType,
                            integrationName: props.translationsIntegrationName,
                            appStoreVersionExists: props.appStoreVersionExists,
                        })}

                    {/* Description */}
                    {createCollapsiblePanel({
                        id: props.initialValues.id,
                        key: CollapseKeys.Description,
                        displayLocale: props.locale,
                        title: 'Description',
                        tooltip: 'A description of your app, detailing features and functionality.',
                        featureSection: FeatureSection.MetadataDescription,
                        children: (
                            <Form.Item
                                name="description"
                                rules={[
                                    {
                                        max: descriptionMaxLength,
                                        message: `Description must be ${descriptionMaxLength} characters or less.`,
                                    },
                                ]}
                            >
                                <Input.TextArea
                                    rows={8}
                                    placeholder={'Add description'}
                                    disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                    style={{ minWidth: 500, height: 150, position: 'relative' }}
                                />
                            </Form.Item>
                        ),
                        releaseStepType: props.releaseStepType,
                        appId: props.appId,
                        releaseId: props.releaseId,
                        onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                        isDisabled: props.shouldDisableMetadataEditing || props.isLoading,
                        fieldName: 'description',
                        maxLength: descriptionMaxLength,
                        translationStatus: props.translationStatus,
                        appStoreType: props.appStoreType,
                        integrationName: props.translationsIntegrationName,
                        appStoreVersionExists: props.appStoreVersionExists,
                    })}

                    {/* Video URL (Google Play only) */}
                    {isGooglePlayStore &&
                        createCollapsiblePanel({
                            id: props.initialValues.id,
                            key: CollapseKeys.VideoUrl,
                            displayLocale: props.locale,
                            title: 'Video URL',
                            tooltip:
                                'Add a video by entering a YouTube URL. This video must be public or unlisted, ads must be turned off, it must not be age restricted, and it should be landscape.',
                            featureSection: FeatureSection.MetadataSubtitle,
                            children: (
                                <Form.Item
                                    name="videoUrl"
                                    rules={[
                                        {
                                            max: videoUrlMaxLength,
                                            message: `Video URL must be ${videoUrlMaxLength} characters or less.`,
                                        },
                                        {
                                            pattern: new RegExp('^(https?://)?(www.youtube.com|youtu.?be)/.+$', 'i'),
                                            message: 'Please enter a valid YouTube URL.',
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={'https://www.youtube.com/watch?v=XXXXXXXXXXX'}
                                        disabled={props.shouldDisableMetadataEditing || props.isLoading}
                                    />
                                </Form.Item>
                            ),
                            releaseStepType: props.releaseStepType,
                            appId: props.appId,
                            releaseId: props.releaseId,
                            onApplyChangesToAllLocales: props.onApplyFieldToAllLocales,
                            isDisabled: props.shouldDisableMetadataEditing || props.isLoading,
                            fieldName: 'videoUrl',
                            maxLength: videoUrlMaxLength,
                            minLength: 0,
                            translationStatus: props.translationStatus,
                            appStoreType: props.appStoreType,
                            integrationName: props.translationsIntegrationName,
                            appStoreVersionExists: props.appStoreVersionExists,
                        })}
                </Collapse>
            </div>
        </ExtendedForm>
    );
});
